import React, {useMemo, useState} from 'react';

import {
  AnalysisTemplateSelectorTemplate,
  useAnalysisTemplatesWithFilter,
} from 'analysis-tool/graphql/AnalysisTemplateSelectorQuery';
import {ChipSize} from 'ui-library/chips/Chip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import ChipSelectInput, {ChipSelectInputProps} from 'ui-library/inputs/ChipSelectInput';
import {OptionData} from 'ui-library/menus/MenuConstants';

export interface MultiAnalysisTemplateSelectorProps extends Pick<
  ChipSelectInputProps,
  'autoFocus' | 'className' | 'disabled' | 'isReadOnly' | 'onBlur' | 'onKeyDown' | 'renderChips'
> {
  filterTemplates?: (template: AnalysisTemplateSelectorTemplate) => boolean;
  selectedItems: Array<AnalysisTemplateSelectorTemplate>;
  onChange: (selectedItems: Array<AnalysisTemplateSelectorTemplate>) => void;
  hideSelectedResults?: boolean;
  shouldAllowMultiple?: boolean;
}

export default function MultiAnalysisTemplateSelector(
  props: MultiAnalysisTemplateSelectorProps
): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('');

  const {loading, validTemplates} = useAnalysisTemplatesWithFilter({
    filterTemplates: props.filterTemplates,
  });

  const filteredTemplateOptions = useMemo((): Array<OptionData> => {
    const lowerQuery = searchQuery.trim().toLowerCase();
    const selectedIds = props.hideSelectedResults
      ? new Set(props.selectedItems.map((item) => item.id))
      : null;

    return validTemplates.reduce((acc, template) => {
      if (lowerQuery && !template.name.toLowerCase().includes(lowerQuery)) {
        return acc;
      }

      if (selectedIds && selectedIds.has(template.id)) {
        return acc;
      }

      acc.push({
        name: template.name,
        value: template,
      });

      return acc;
    }, [] as Array<OptionData>);
  }, [validTemplates, searchQuery, props.selectedItems, props.hideSelectedResults]);

  if (loading) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <ChipSelectInput
      {...props}
      isMulti={props.shouldAllowMultiple}
      optionDatas={filteredTemplateOptions}
      value={props.selectedItems}
      query={searchQuery}
      placeholder="Search for analysis templates..."
      onQueryChange={(e) => {
        setSearchQuery(e.target.value);
      }}
      onSelect={(template: AnalysisTemplateSelectorTemplate) => {
        if (props.shouldAllowMultiple) {
          props.onChange([...props.selectedItems, template]);
        } else {
          props.onChange([template]);
        }
        if (props.shouldAllowMultiple) {
          setSearchQuery('');
        }
      }}
      onRemove={(template: AnalysisTemplateSelectorTemplate) => {
        props.onChange(props.selectedItems.filter((item) => item.id !== template.id));
      }}
      getChipProps={(template: AnalysisTemplateSelectorTemplate) => ({
        text: template.name,
        size: ChipSize.SMALL,
      })}
    />
  );
}
